// pages/TarjetaComedor.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Form,
  Input,
  Select,
  Space,
  Typography,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Card,
  DatePicker,
  Statistic,
  Badge,
  Divider,
  Modal
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  PrinterOutlined,
  CoffeeOutlined,
  HomeOutlined,
  UserOutlined,
  CalendarOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  BarcodeOutlined,
  IdcardOutlined,
  FilePdfOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

dayjs.locale('es');

const TarjetaComedor = () => {
  const [tarjetas, setTarjetas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [opciones, setOpciones] = useState({ reservas: [], habitaciones: [] });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [printModalVisible, setPrintModalVisible] = useState(false);
  const [tarjetaSeleccionada, setTarjetaSeleccionada] = useState(null);
  
  // Estados para filtros
    const [filtros, setFiltros] = useState({
        fechaEntrada: dayjs().format('YYYY-MM-DD'),
        fechaEntradaFin: dayjs().format('YYYY-MM-DD'),
        numReserva: null,
        numHabitacion: null
    });
  
  const [sortedInfo, setSortedInfo] = useState({});
  const printRef = useRef();

  // Configuración de columnas
  const columns = [
    {
      title: 'Habitación',
      dataIndex: 'Numhab',
      key: 'Numhab',
      width: 100,
      sorter: (a, b) => (a.Numhab || '').localeCompare(b.Numhab || ''),
      sortOrder: sortedInfo.columnKey === 'Numhab' && sortedInfo.order,
      render: (numhab) => (
        <Tag color="blue" style={{ fontSize: '14px', fontWeight: 'bold' }}>
          <HomeOutlined style={{ marginRight: 4 }} />
          {numhab}
        </Tag>
      ),
    },
    {
      title: 'Reserva',
      dataIndex: 'NumReserva',
      key: 'NumReserva',
      width: 100,
      sorter: (a, b) => (a.NumReserva || 0) - (b.NumReserva || 0),
      sortOrder: sortedInfo.columnKey === 'NumReserva' && sortedInfo.order,
      render: (num) => (
        <Tag color="purple">
          <BarcodeOutlined style={{ marginRight: 4 }} />
          {num}
        </Tag>
      ),
    },
    {
      title: 'Huésped',
      dataIndex: 'Nombre',
      key: 'Nombre',
      width: 200,
      sorter: (a, b) => (a.Nombre || '').localeCompare(b.Nombre || ''),
      sortOrder: sortedInfo.columnKey === 'Nombre' && sortedInfo.order,
      render: (nombre, record) => (
        <div>
          <Text strong>{nombre}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            <IdcardOutlined /> {record.CodParti}
          </Text>
        </div>
      ),
    },
    {
      title: 'Régimen',
      dataIndex: 'regimen',
      key: 'regimen',
      width: 120,
      render: (regimen, record) => (
        <Tooltip title={record.serviciosIncluidos?.join(', ')}>
          <Tag color={getRegimenColor(regimen)}>
            <CoffeeOutlined style={{ marginRight: 4 }} />
            {regimen}
          </Tag>
        </Tooltip>
      ),
    },
    {
      title: 'Pax',
      dataIndex: 'Pax',
      key: 'Pax',
      width: 60,
      align: 'center',
      render: (pax) => (
        <Badge count={pax} showZero style={{ backgroundColor: '#52c41a' }} />
      ),
    },
    {
      title: 'Estancia',
      key: 'estancia',
      width: 180,
      render: (_, record) => (
        <div>
          <div>
            <CalendarOutlined style={{ marginRight: 4, color: '#52c41a' }} />
            <Text type="success">In: {record.entrada ? dayjs(record.entrada).format('DD/MM/YYYY') : '-'}</Text>
          </div>
          <div>
            <CalendarOutlined style={{ marginRight: 4, color: '#ff4d4f' }} />
            <Text type="danger">Out: {record.salida ? dayjs(record.salida).format('DD/MM/YYYY') : '-'}</Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Servicios',
      key: 'servicios',
      width: 200,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          {record.desayunodesde && (
            <Tooltip title={`${record.desayunodesde} - ${record.desayunohasta}`}>
              <Tag icon={<CoffeeOutlined />} color="orange">Desayuno</Tag>
            </Tooltip>
          )}
          {record.almuerzodesde && (
            <Tooltip title={`${record.almuerzodesde} - ${record.almuerzohasta}`}>
              <Tag icon={<CoffeeOutlined />} color="green">Almuerzo</Tag>
            </Tooltip>
          )}
          {record.cenadesde && (
            <Tooltip title={`${record.cenadesde} - ${record.cenahasta}`}>
              <Tag icon={<CoffeeOutlined />} color="blue">Cena</Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Imprimir Tarjeta">
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              size="small"
              onClick={() => handlePrintSingle(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const getRegimenColor = (regimen) => {
    const colores = {
      'AD': 'default',
      'AD+': 'blue',
      'MP': 'orange',
      'PC': 'green',
      'TI': 'gold',
      'H': 'default',
      'M': 'orange',
      'P': 'green',
      'T': 'gold'
    };
    return colores[regimen] || 'default';
  };

  useEffect(() => {
    fetchOpciones();
    fetchTarjetas();
  }, []);

  const fetchOpciones = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/tarjetacomedor/opciones`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setOpciones(response.data.data);
      }
    } catch (error) {
      console.error('❌ Error cargando opciones:', error);
    }
  };

  const fetchTarjetas = async (params = {}) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const queryParams = {
        fechaEntrada: filtros.fechaEntrada,
        fechaEntradaFin: filtros.fechaEntradaFin,
        numReserva: filtros.numReserva,
        numHabitacion: filtros.numHabitacion,
        sortField: params.sortField || sortedInfo.field || 'Numhab',
        sortOrder: params.sortOrder || (sortedInfo.order === 'ascend' ? 'ASC' : 'DESC')
      };
      
      // Limpiar parámetros nulos
      Object.keys(queryParams).forEach(key => {
        if (queryParams[key] === null || queryParams[key] === undefined) {
          delete queryParams[key];
        }
      });
      
      const response = await axios.get(`${BASE_URL}/api/tarjetacomedor`, {
        headers: { Authorization: `Bearer ${token}` },
        params: queryParams
      });

      setTarjetas(response.data.data || []);
      
    } catch (error) {
      console.error('❌ Error cargando tarjetas:', error);
      message.error('Error al cargar las tarjetas de comedor');
      setTarjetas([]);
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setSortedInfo(sorter);
    
    const params = {
      sortField: sorter.field,
      sortOrder: sorter.order === 'ascend' ? 'ASC' : 'DESC'
    };
    
    fetchTarjetas(params);
  };

  const handleSearch = () => {
    setSearchLoading(true);
    fetchTarjetas();
  };

    const handlePrintAll = () => {
        if (tarjetas.length === 0) {
            message.warning('No hay tarjetas para imprimir');
            return;
        }
        setTarjetaSeleccionada(tarjetas);
        setPrintModalVisible(true);
    };

  const handleReset = () => {
    setFiltros({
        fechaEntrada: dayjs().format('YYYY-MM-DD'),
        fechaEntradaFin: dayjs().format('YYYY-MM-DD'),
        numReserva: null,
        numHabitacion: null
    });
    setSortedInfo({});
    setSelectedRowKeys([]);
    setTimeout(() => fetchTarjetas(), 0);
  };

  const handlePrintSingle = (record) => {
    setTarjetaSeleccionada(record);
    setPrintModalVisible(true);
  };

  const handlePrintMultiple = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Seleccione al menos una tarjeta para imprimir');
      return;
    }
    
    const tarjetasSeleccionadas = tarjetas.filter(t => selectedRowKeys.includes(t.CodParti + t.NumReserva + t.Linea));
    setTarjetaSeleccionada(tarjetasSeleccionadas);
    setPrintModalVisible(true);
  };

  const handlePrint = () => {
    message.info('Función de impresión en desarrollo');
    // Aquí implementaremos la impresión según el diseño solicitado
    window.print();
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys) => {
      setSelectedRowKeys(selectedRowKeys);
    },
    getCheckboxProps: (record) => ({
      name: record.Nombre,
    }),
  };

  // Componente de filtros
  const filterBar = (
    <Card style={{ marginBottom: 16, borderRadius: '8px' }} bodyStyle={{ padding: '16px 24px' }}>
      <Row gutter={[16, 16]} align="middle">
        <Col span={6}>
          <Form.Item label="Fecha Entrada" style={{ marginBottom: 0 }}>
            <RangePicker
              style={{ width: '100%' }}
              value={[dayjs(filtros.fechaEntrada), dayjs(filtros.fechaEntradaFin)]}
              onChange={(dates) => {
                if (dates) {
                  setFiltros({
                    ...filtros,
                    fechaEntrada: dates[0]?.format('YYYY-MM-DD'),
                    fechaEntradaFin: dates[1]?.format('YYYY-MM-DD')
                  });
                } else {
                  setFiltros({
                    ...filtros,
                    fechaEntrada: null,
                    fechaEntradaFin: null
                  });
                }
              }}
              format="DD/MM/YYYY"
              placeholder={['Desde', 'Hasta']}
            />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item label="Nº Reserva" style={{ marginBottom: 0 }}>
            <Select
              style={{ width: '100%' }}
              placeholder="Seleccionar"
              value={filtros.numReserva}
              onChange={(value) => setFiltros({ ...filtros, numReserva: value })}
              allowClear
              showSearch
              filterOption={(input, option) => 
                option?.children?.toString().toLowerCase().includes(input.toLowerCase())
              }
            >
              {opciones.reservas.map(num => (
                <Option key={num} value={num}>{num}</Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item label="Nº Habitación" style={{ marginBottom: 0 }}>
            <Select
              style={{ width: '100%' }}
              placeholder="Seleccionar"
              value={filtros.numHabitacion}
              onChange={(value) => setFiltros({ ...filtros, numHabitacion: value })}
              allowClear
              showSearch
            >
              {opciones.habitaciones.map(hab => (
                <Option key={hab} value={hab}>{hab}</Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={10} style={{ textAlign: 'right' }}>
          <Space>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
              loading={searchLoading}
            >
              Buscar
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleReset}
            >
              Restablecer
            </Button>
            <Button
                type="default"
                icon={<PrinterOutlined />}
                onClick={() => handlePrintAll()}
            >
                Imprimir Todo
            </Button>            
            <Button
              type="default"
              icon={<PrinterOutlined />}
              onClick={handlePrintMultiple}
              disabled={selectedRowKeys.length === 0}
            >
              Imprimir Selección ({selectedRowKeys.length})
            </Button>
          </Space>
        </Col>
      </Row>
    </Card>
  );

  // Estadísticas
  const estadisticas = (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col span={6}>
        <Statistic 
          title="Total Huéspedes" 
          value={tarjetas.length}
          prefix={<TeamOutlined />}
          valueStyle={{ color: '#1890ff' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Habitaciones Ocupadas" 
          value={[...new Set(tarjetas.map(t => t.Numhab))].length}
          prefix={<HomeOutlined />}
          valueStyle={{ color: '#52c41a' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Total Pax" 
          value={tarjetas.reduce((sum, t) => sum + (t.Pax || 0), 0)}
          prefix={<UserOutlined />}
          valueStyle={{ color: '#fa8c16' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Con Desayuno" 
          value={tarjetas.filter(t => t.desayunodesde).length}
          suffix={`/ ${tarjetas.length}`}
          prefix={<CoffeeOutlined />}
          valueStyle={{ color: '#722ed1' }}
        />
      </Col>
    </Row>
  );

  // Modal de previsualización de impresión
  const renderPrintPreview = () => {
  if (!tarjetaSeleccionada) return null;
  
  const tarjetasArray = Array.isArray(tarjetaSeleccionada) ? tarjetaSeleccionada : [tarjetaSeleccionada];
  
  // Función para calcular los días y servicios
  const calcularServicios = (tarjeta) => {
    const entrada = dayjs(tarjeta.entrada);
    const salida = dayjs(tarjeta.salida);
    const dias = salida.diff(entrada, 'day');
    
    const servicios = [];
    for (let i = 0; i <= 30; i++) {
      const fechaActual = entrada.add(i, 'day');
      const diaNum = fechaActual.date();
      
      let desayuno = '';
      let almuerzo = '';
      let cena = '';
      
      if (i <= dias) {
        const regimen = tarjeta.regimen?.substring(0, 1) || '';
        const serEntrada = tarjeta.serentrada || '';
        const serSalida = tarjeta.sersalida || '';
        
        // Primer día (entrada)
        if (i === 0) {
          if (serEntrada === 'DE') desayuno = 'X';
          else if (serEntrada === 'AL') almuerzo = 'X';
          else if (serEntrada === 'CE') cena = 'X';
        }
        // Último día (salida)
        else if (i === dias) {
          if (['H','M','P','T'].includes(regimen)) desayuno = 'X';
          if (serSalida === 'AL') almuerzo = 'X';
          else if (serSalida === 'CE') cena = 'X';
        }
        // Días intermedios
        else {
          if (['H','M','P','T'].includes(regimen)) desayuno = 'X';
          if (['P','T'].includes(regimen)) almuerzo = 'X';
          if (['M','P','T'].includes(regimen)) cena = 'X';
        }
      }
      
      servicios.push({ dia: diaNum, desayuno, almuerzo, cena });
    }
    return servicios;
  };

  return (
    <div ref={printRef} style={{ padding: '10px', fontFamily: 'Arial, sans-serif' }}>
      {tarjetasArray.map((tarjeta, idx) => {
        const servicios = calcularServicios(tarjeta);
        const entrada = dayjs(tarjeta.entrada);
        const salida = dayjs(tarjeta.salida);
        const regimenNombre = {
          'AD': 'Alojamiento', 'AD+': 'Hab. y Desayuno', 'MP': 'Media Pensión',
          'PC': 'Pensión Completa', 'TI': 'Todo Incluido',
          'H': 'Habitación', 'M': 'Media Pensión', 'P': 'Pensión Completa', 'T': 'Todo Incluido'
        }[tarjeta.regimen] || tarjeta.regimen;
        
        return (
          <div key={idx} style={{ 
            pageBreakAfter: idx < tarjetasArray.length - 1 ? 'always' : 'auto',
            padding: '15px',
            border: '1px solid #ccc',
            marginBottom: '10px'
          }}>
            {/* Cabecera - Nombre Hotel */}
            <div style={{ textAlign: 'right', fontSize: '14px', fontWeight: 'bold', marginBottom: '10px' }}>
              HOTEL EJEMPLO
            </div>
            
            {/* Información del huésped */}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px' }}>
              <tbody>
                <tr>
                  <td style={{ fontWeight: 'bold', width: '80px' }}>Nombre:</td>
                  <td style={{ width: '250px' }}>{tarjeta.Nombre}</td>
                  <td style={{ fontWeight: 'bold', width: '60px' }}>Llegada:</td>
                  <td style={{ width: '100px' }}>{entrada.format('DD/MM/YYYY')}</td>
                  <td style={{ fontWeight: 'bold', width: '50px' }}>Salida:</td>
                  <td>{salida.format('DD/MM/YYYY')}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 'bold' }}>Habitación:</td>
                  <td>{tarjeta.Numhab}</td>
                  <td style={{ fontWeight: 'bold' }}>Pax:</td>
                  <td>{tarjeta.Pax}</td>
                  <td style={{ fontWeight: 'bold' }}>Régimen:</td>
                  <td>{regimenNombre}</td>
                </tr>
              </tbody>
            </table>
            
            {/* Horario del comedor */}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '15px', border: '1px solid #000' }}>
              <thead>
                <tr>
                  <th colSpan={3} style={{ border: '1px solid #000', padding: '5px', textAlign: 'center' }}>
                    Horario del comedor
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ fontWeight: 'bold', padding: '3px' }}>Desayuno</td>
                  <td style={{ textAlign: 'right', padding: '3px' }}>{tarjeta.desayunodesde || '-'}</td>
                  <td style={{ textAlign: 'center', padding: '3px' }}>-</td>
                  <td style={{ padding: '3px' }}>{tarjeta.desayunohasta || '-'}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 'bold', padding: '3px' }}>Comida</td>
                  <td style={{ textAlign: 'right', padding: '3px' }}>{tarjeta.almuerzodesde || '-'}</td>
                  <td style={{ textAlign: 'center', padding: '3px' }}>-</td>
                  <td style={{ padding: '3px' }}>{tarjeta.almuerzohasta || '-'}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 'bold', padding: '3px' }}>Cena</td>
                  <td style={{ textAlign: 'right', padding: '3px' }}>{tarjeta.cenadesde || '-'}</td>
                  <td style={{ textAlign: 'center', padding: '3px' }}>-</td>
                  <td style={{ padding: '3px' }}>{tarjeta.cenahasta || '-'}</td>
                </tr>
              </tbody>
            </table>
            
            {/* Tabla de días y servicios */}
            <div style={{ marginBottom: '15px' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Comidas Concertadas</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                <thead>
                  <tr>
                    <th style={{ border: '1px solid #000', padding: '3px', textAlign: 'center', width: '50px' }}>Días</th>
                    {[...Array(31)].map((_, i) => (
                      <th key={i} style={{ border: '1px solid #000', padding: '3px', textAlign: 'center', width: '22px' }}>
                        {servicios[i]?.dia || ''}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ border: '1px solid #000', padding: '3px', fontWeight: 'bold' }}>Desayuno</td>
                    {servicios.map((s, i) => (
                      <td key={i} style={{ border: '1px solid #000', padding: '3px', textAlign: 'center' }}>
                        {s.desayuno}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid #000', padding: '3px', fontWeight: 'bold' }}>Comida</td>
                    {servicios.map((s, i) => (
                      <td key={i} style={{ border: '1px solid #000', padding: '3px', textAlign: 'center' }}>
                        {s.almuerzo}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid #000', padding: '3px', fontWeight: 'bold' }}>Cena</td>
                    {servicios.map((s, i) => (
                      <td key={i} style={{ border: '1px solid #000', padding: '3px', textAlign: 'center' }}>
                        {s.cena}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
            
            {/* Check-out info */}
            <div style={{ border: '1px solid #000', padding: '10px', marginTop: '10px' }}>
              <span style={{ fontWeight: 'bold' }}>CHECK-OUT</span>
              <span style={{ marginLeft: '20px' }}>Deberán dejar la habitación libre a las </span>
              <span style={{ fontWeight: 'bold', marginLeft: '10px' }}>12 HORAS</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

  const renderPrintPreview2= () => {
    if (!tarjetaSeleccionada) return null;
    
    const tarjetasArray = Array.isArray(tarjetaSeleccionada) ? tarjetaSeleccionada : [tarjetaSeleccionada];
    
    return (
      <div ref={printRef} style={{ padding: '20px' }}>
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <Title level={3}>
            <CoffeeOutlined /> Tarjetas de Comedor
          </Title>
          <Text type="secondary">Fecha: {dayjs().format('DD/MM/YYYY HH:mm')}</Text>
        </div>
        
        <Row gutter={[16, 16]}>
          {tarjetasArray.map((tarjeta, index) => (
            <Col span={12} key={index}>
              <Card 
                style={{ 
                  marginBottom: '16px',
                  border: '2px solid #1890ff',
                  borderRadius: '12px'
                }}
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>
                      <HomeOutlined /> Habitación: {tarjeta.Numhab}
                    </span>
                    <Tag color="blue">Reserva: {tarjeta.NumReserva}</Tag>
                  </div>
                }
              >
                <div style={{ marginBottom: '12px' }}>
                  <Title level={4}>{tarjeta.Nombre}</Title>
                  <Space split={<Divider type="vertical" />}>
                    <Text><IdcardOutlined /> {tarjeta.CodParti}</Text>
                    <Text><TeamOutlined /> {tarjeta.Pax} pax</Text>
                    <Tag color={getRegimenColor(tarjeta.regimen)}>{tarjeta.regimenCompleto}</Tag>
                  </Space>
                </div>
                
                <Divider orientation="left">Estancia</Divider>
                <Row gutter={16}>
                  <Col span={12}>
                    <Text type="success">
                      <CalendarOutlined /> Entrada: {dayjs(tarjeta.entrada).format('DD/MM/YYYY')}
                    </Text>
                  </Col>
                  <Col span={12}>
                    <Text type="danger">
                      <CalendarOutlined /> Salida: {dayjs(tarjeta.salida).format('DD/MM/YYYY')}
                    </Text>
                  </Col>
                </Row>
                
                <Divider orientation="left">Servicios Incluidos</Divider>
                <Row gutter={8}>
                  {tarjeta.desayunodesde && (
                    <Col span={8}>
                      <Card size="small" style={{ backgroundColor: '#fff7e6' }}>
                        <div style={{ textAlign: 'center' }}>
                          <CoffeeOutlined style={{ fontSize: '20px', color: '#fa8c16' }} />
                          <div><strong>Desayuno</strong></div>
                          <div style={{ fontSize: '12px' }}>
                            {tarjeta.desayunodesde} - {tarjeta.desayunohasta}
                          </div>
                        </div>
                      </Card>
                    </Col>
                  )}
                  {tarjeta.almuerzodesde && (
                    <Col span={8}>
                      <Card size="small" style={{ backgroundColor: '#f6ffed' }}>
                        <div style={{ textAlign: 'center' }}>
                          <CoffeeOutlined style={{ fontSize: '20px', color: '#52c41a' }} />
                          <div><strong>Almuerzo</strong></div>
                          <div style={{ fontSize: '12px' }}>
                            {tarjeta.almuerzodesde} - {tarjeta.almuerzohasta}
                          </div>
                        </div>
                      </Card>
                    </Col>
                  )}
                  {tarjeta.cenadesde && (
                    <Col span={8}>
                      <Card size="small" style={{ backgroundColor: '#e6f7ff' }}>
                        <div style={{ textAlign: 'center' }}>
                          <CoffeeOutlined style={{ fontSize: '20px', color: '#1890ff' }} />
                          <div><strong>Cena</strong></div>
                          <div style={{ fontSize: '12px' }}>
                            {tarjeta.cenadesde} - {tarjeta.cenahasta}
                          </div>
                        </div>
                      </Card>
                    </Col>
                  )}
                </Row>
                
                <Divider />
                <div style={{ textAlign: 'right' }}>
                  <Text type="secondary" style={{ fontSize: '11px' }}>
                    Generado: {dayjs().format('DD/MM/YYYY HH:mm')}
                  </Text>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    );
  };

  return (
    <>
      <PageContainer
        title="Tarjetas de Comedor"
        subtitle={`${tarjetas.length} huéspedes con servicios de comedor`}
        icon={<CoffeeOutlined />}
        loading={loading}
        onRefresh={fetchTarjetas}
        badgeCount={tarjetas.length}
        badgeStatus="processing"
        extra={
          <Space>
            <Button
              icon={<PrinterOutlined />}
              onClick={handlePrintMultiple}
              disabled={selectedRowKeys.length === 0}
            >
              Imprimir ({selectedRowKeys.length})
            </Button>
          </Space>
        }
        cardStyle={{
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
        }}
      >
        {/* Estadísticas */}
        {estadisticas}
        
        {/* Filtros */}
        {filterBar}
        
        {/* Tabla */}
        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={tarjetas}
          rowKey={(record) => record.CodParti + record.NumReserva + record.Linea}
          loading={loading}
        //   onChange={handleTableChange}
          pagination={false}
          scroll={{ x: 1300, y: 500 }}
          style={{
            borderRadius: '8px',
            overflow: 'hidden'
          }}
          size="middle"
          locale={{
            emptyText: 'No se encontraron tarjetas de comedor'
          }}
        />
      </PageContainer>

      {/* Modal de previsualización de impresión */}
      <Modal
        title={
          <span>
            <PrinterOutlined /> Vista Previa de Impresión
          </span>
        }
        open={printModalVisible}
        onCancel={() => setPrintModalVisible(false)}
         width={1200}
        footer={[
          <Button key="cancel" onClick={() => setPrintModalVisible(false)}>
            Cancelar
          </Button>,
          <Button 
            key="print" 
            type="primary" 
            icon={<PrinterOutlined />} 
            onClick={handlePrint}
          >
            Imprimir
          </Button>
        ]}
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
      >
        {renderPrintPreview()}
      </Modal>
    </>
  );
};

export default TarjetaComedor;